import { NormalizedCacheObject } from 'apollo-cache-inmemory'
import ApolloClient from 'apollo-client'
import gql from 'graphql-tag'
import _Vue from 'vue'
import VueRouter, { NavigationGuardNext, Route } from 'vue-router'
import { Store } from 'vuex'
import { validate as isUUID } from 'uuid'

// TYPES
type LoginAction = 'continue' | 'accountSetupRequired'

type Tokens = {
  accessToken: string;
  refreshToken: string;
}

type LoginResponse = {
  error?: string;
  action?: LoginAction;
  tokens?: Tokens;
  user?: any;
}

type MoonpullUserCompany = {
  id?: string;
  isSuperAdmin?: boolean | undefined;
  name: string;
  apiAccess?: boolean;
  numUsers?: number;
  userCount?: number;
  validDomains?: string[];
  accessStartsAt?: number;
  accessEndsAt?: number;
  createdAt?: number;
  updatedAt?: number;
  networkResponsibility?: {
    network?: string;
  }[];
  networkAccess?: {
    network?: string;
    allowedProgIds?: string[];
  }[];
}

type MoonpullUser = {
  id: string;
  cognitoId: string;
  company: MoonpullUserCompany;
  name: string;
  title: string;
  email: string;
  status: 'enabled' | 'disabled';
  isCompanyOwner: boolean;
  createdAt: number;
  updatedAt: number;
}

type MoonpullSession = null | {
  tokens: Tokens;
  user: any;
}

type BatchMeta = {
  queueLetter?: string;
  name?: string;
  description?: string;
  mobile?: boolean;
  userAgent?: boolean;
  vpn?: boolean;
  setLoyaltyContext?: boolean | 'true' | 'false';
}

interface Moonpull {
  // Auth
  getAuthHeader(): { Authorization: string };
  getSsoUrl(): Promise<string>;
  exchangeSsoCode(code: string): Promise<void>;
  login(username: string, password: string): Promise<void>;
  firstTimeAccountSetup(username: string, password: string, newPassword: string): Promise<boolean>;
  isLoggedIn(): Promise<boolean>;
  logout(): void;
  getCurrentUserSession(): MoonpullUser;
  currentUserIsSuperAdmin(): boolean;
  currentUserIsCompanyOwner(): boolean;
  initResetPassword(email: string): Promise<boolean>;
  getUserFromResetPasswordToken(resetToken: string): Promise<boolean>;
  resetPassword(resetToken: string, newPassword: string): Promise<boolean>;
  isServerDown(): Promise<boolean>;
  
  // Auditing
  auditFromCsv(csv: any, batchMeta: BatchMeta): Promise<{ batchId: string }>;
  audit(links: Array<String>, batchMeta: BatchMeta): Promise<{ batchId: string }>;
  rerunBatch(batchId: string): Promise<boolean>;
  rerunAudit(batchId: string, auditId: string): Promise<boolean>;
  getAllBatches(): Promise<any>;
  getSingleBatch(batchId: string): Promise<any>;
  getSingleAudit(batchId: string, auditId: string): Promise<any>;
  loadScreenshots(auditId: string): Promise<string | boolean>;
  downloadSingleBatchCSV(batchId: string): Promise<any>;
  downloadingTechReportCSV(advertiser: string): Promise<any>;

  // Account management
  getCompanies(): Promise<Array<MoonpullUserCompany> | void>;
  createCompany(newCompany: MoonpullUserCompany): Promise<boolean | void>;
  getCompany(companyId: string): Promise<MoonpullUserCompany | void>;
  updateCompany(companyId: string, updatedCompany: MoonpullUserCompany): Promise<boolean | void>;
  addExtraToCompany(companyId: string, thisMonthExtra: number): Promise<boolean>;
  getUsers(): Promise<Array<MoonpullUser> | void>;
  createUser(newUser: MoonpullUser): Promise<boolean | void>;
  getUser(userId: string): Promise<MoonpullUser | void>;
  updateUser(userId: string, updatedUser: MoonpullUser): Promise<boolean | void>;
  deleteUser(userId: string): Promise<boolean | void>;

  // CMS
  // TODO: Method typings
  addClientComment(batchId: string, auditId: string, comment: string): Promise<boolean>;
  addMoonpullComment(batchId: string, auditId: string, comment: string): Promise<boolean>;

  getNetworks(): Promise<any>;
  getNetworksComments(): Promise<any>;
  updateNetworkComments(networkComments: any): Promise<any>;
  getNetwork(networkId: string): Promise<any>;
  updateNetwork(updatedNetwork: any): Promise<any>;
  getNetworkActiveErrors(networkId: string): Promise<any>;
  getNetworkSuccessCriteria(): Promise<any>;

  getAdvertisers(): Promise<any>;
  getAdvertiser(advertiserId: string): Promise<any>;
  createAdvertiser(newAdvertiser: any): Promise<any>;
  updateAdvertiser(updatedAdvertiser: any): Promise<any>;
  deleteAdvertiser(advertiserId: any): Promise<any>;
  uploadAdvertisers(networkName: string, formData: FormData): Promise<any>;

  getErrors(): Promise<any>;
  getError(errorId: string): Promise<any>;
  updateError(error: any): Promise<boolean | void>;
  toggleMoonpullOnlyError(errorId: string): Promise<boolean>;
  toggleHideFromDownload(errorId: string): Promise<boolean>;

  getNarratives(): Promise<any>;
  getNarrative(narrativeId: string): Promise<any>;

  getPlatformContent(): Promise<any>;

  auditHasError(audit: any, error: string): boolean;
  downloadCsv(csvData: any, fileName: string): void;

  getLogging(): Promise<string>;

  currentUserCompanyIs(companyName: string): boolean;

  redirectToNewBatchIdIfNecessary(batchId: string): Promise<void>;
  redirectToNewAuditIdIfNecessary(auditId: string, name: string): Promise<void>;

  getAssurance(link: string): Promise<any>;
  generateAssurancesFromBatchIds(apiKey: string, batchIds: string): Promise<any>;
  loadAssuranceEmbedScript(settings?: any): void;

  getOutputReportPDF(auditId: string): Promise<any>;
  getValueCalc(data: any): Promise<any>;
  getWarningScenarioCalc(data: any): Promise<any>;
  getAdvertiserTrackingStatusPDF(auditId: string): Promise<any>;
  getConsentOnlyPDF(auditId: string): Promise<any>;
  getImmediatePDF(auditId: string): Promise<any>;
  getNoCookiePDF(auditId: string): Promise<any>;
  getUserConsentRefreshPDF(auditId: string): Promise<any>;
  getNoAniPDF(auditId: string): Promise<any>;
  downloadFile(type: string, filename: string, data: any): void;

  currentUserHasRole(roleId: string): boolean;
  currentUserIsNetworkUser(): boolean;
  currentUserIsGlobalNetworkUser(): boolean;
  currentUserIsMoonpullShareUser(): boolean;
  loadAccessControl(): Promise<void>;
  currentUserHasAccessTo(resource: 'moonpullVerification' | 'enterpriseReporting' | 'api' | 'advertiserManagement' | 'scheduling' | 'geekFeatures'): boolean;
  currentUserHasShareAccess(): boolean;

  copyToClipboard(content: string, successMessage?: string): void;

  nuxtUrl(path: string): string;
}

declare module 'vue/types/vue' {
  interface Vue {
    $moonpull: Moonpull
  }
}

export const MOONPULL_SESSION_NAME = 'moonpullSession'

export default function Moonpull(Vue: typeof _Vue, { router, store, apolloClient }: { router: VueRouter; store: Store<any>; apolloClient: ApolloClient<NormalizedCacheObject> }): void {
 
  // GLOBALS
  const apiUrl = process.env.VUE_APP_MOONPULL_API_ENDPOINT
  const sessionName = MOONPULL_SESSION_NAME
  let moonpullSession: MoonpullSession = null
  let accessControl: any = null

  // FUNCTIONS
  const createMoonpullSession = (tokens: Tokens, user: any) => {
    moonpullSession = { tokens, user }
    localStorage.setItem(sessionName, JSON.stringify(moonpullSession))
  }

  const destroyMoonpullSession = () => {
    localStorage.removeItem(sessionName)
    moonpullSession = null
    router.push({ name: 'login' })
  }

  const getAuthHeader = () => {
    let authHeader = {
      'Authorization': 'None'
    }
    if (moonpullSession) {
      authHeader.Authorization = `Bearer ${moonpullSession.tokens.accessToken}`
    }
    return authHeader
  }

  const fetchData = async (url: string, body: any = null, headers = {}, method = "GET", asBlob = false) => {
    const derivedUrl = `${apiUrl}/${url}`

    const realHeaders = {
      ...getAuthHeader(),
      ...(headers || {})
    }

    const options: object = {
      method,
      headers: realHeaders,
      body
    };

    try {
      const res = await fetch(derivedUrl, options)

      let response, isJson = false
      if (asBlob) {
        response = await res.blob()
      }
      else {
        response = await res.text()
        try {
          response = JSON.parse(response)
          isJson = true
        } catch {}
      }

      if (! res.ok || (isJson && (response.errorMessage || response.error))) {
        
        // Don't dispatch messages for authorisation errors
        if (res.status !== 401) {
          store.dispatch('flashMessage', {
            type: 'danger',
            msg: (! isJson) ? "Something went wrong" : (response.errorMessage || response.error)
          })
        }
        return false
      }
      
      return response
    } catch (e) {
      console.error('------> Error in fetch data:', e);
      store.dispatch('flashMessage', { type: 'danger', msg: 'Something went wrong' })
      return false
    }
  }

  // Reload existing session if it exists
  const existingSession = localStorage.getItem(sessionName)
  if (existingSession) {
    moonpullSession = JSON.parse(existingSession)
  }

  // Register navigation guard on router
  router.beforeEach((to: Route, from: Route, next: NavigationGuardNext<Vue>) => {
    const session = moonpullSession
    if (! session && to.name !== 'login' && to.name !== 'account-setup') {
      next({ name: 'login' })
    }
    else {
      next()
    }
  })

  // Helper for processing login response from multiple sources e.g. username/password, SSO
  const processLoginResponse = async (res: Response, username?: string) => {

    const rawLoginResponse = await res.json()

    if (! res.ok) {
      store.dispatch('flashMessage', { type: 'danger', msg: rawLoginResponse.errorMessage })
      return
    }

    const { error, action, tokens, user } = rawLoginResponse as LoginResponse

    if (error) {
      store.dispatch('flashMessage', { type: 'danger', msg: error })
      return
    }

    if (action === 'accountSetupRequired' && username) {
      router.push({
        name: 'account-setup',
        params: { username }
      })
    }
    else if (action === 'continue') {
      createMoonpullSession(tokens!, user)
      router.push({ name: 'reporting-batches' })
    }
  }
  
  const moonpullApi: Moonpull = {

    //////////
    // AUTH //
    //////////

    getAuthHeader,

    async getSsoUrl() {
      const res = await fetch(`${apiUrl}/auth/sso/url`)
      if (! res.ok) {
        store.dispatch('flashMessage', { type: 'danger', msg: `Unexpected error getting SSO URL` })
        return
      }
      return await res.json()
    },

    async exchangeSsoCode(code) {
      const res = await fetch(`${apiUrl}/auth/sso/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      })
      return await processLoginResponse(res)
    },

    async login(username, password) {
      const res = await fetch(`${apiUrl}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      })
      return await processLoginResponse(res, username)
    },

    async firstTimeAccountSetup(username, password, newPassword) {
      const res = await fetch(`${apiUrl}/auth/setup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password, newPassword })
      })

      if (! res.ok) {
        const json = await res.json()
        store.dispatch('flashMessage', { type: 'danger', msg: json.errorMessage })
        return false
      }

      router.push({ name: 'login' })
      return true
    },

    async isLoggedIn() {
      const res = await fetch(`${apiUrl}/auth/check`, { headers: getAuthHeader() })
      if (! res.ok) {
        // Try to refresh token if Moonpull Session is active
        if (moonpullSession && moonpullSession.tokens && moonpullSession.tokens.refreshToken && moonpullSession.user && moonpullSession.user.email) {
          const refreshRes = await fetch(`${apiUrl}/auth/refresh`, {
            method: 'POST',
            headers: {
              'Authorization': `Refresh ${moonpullSession.tokens.refreshToken}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              username: moonpullSession.user.email
            })
          })

          if (refreshRes.ok) {
            const { error, tokens, user } = await refreshRes.json() as LoginResponse

            if (error) {
              store.dispatch('flashMessage', { type: 'danger', msg: error })
              return false
            }

            createMoonpullSession(tokens!, user)
            return true
          }
          // Hard-logout if refresh fails after check
          else {
            this.logout()
            return false
          }
        }
        return false
      }
      return true
    },

    logout() {
      if (moonpullSession) {
        fetch(`${apiUrl}/auth/logout`, {
          headers: {
            Authorization: `Refresh ${moonpullSession.tokens.refreshToken}`
          }
        })
          .then(() => {
            destroyMoonpullSession()
          })
      }
    },

    getCurrentUserSession() {
      moonpullSession = JSON.parse(localStorage.getItem(sessionName))
      if (! moonpullSession) {
        destroyMoonpullSession()
        return
      }
      return moonpullSession.user
    },

    currentUserIsSuperAdmin() {
      const currentSession = this.getCurrentUserSession();
      if (!currentSession) {
        return false;
      }

      if (currentSession.company && currentSession.company.isSuperAdmin) {
        return true;
      } else {
        return false;
      }
    },

    currentUserIsCompanyOwner() {
      const currentSession = this.getCurrentUserSession();
      if (!currentSession) {
        return false;
      }
      
      if (currentSession.isCompanyOwner) {
        return true;
      } else {
        return false;
      }
    },

    async initResetPassword(email) {

      const res = await fetch(`${apiUrl}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      })

      if (! res.ok) {
        const json = await res.json()
        store.dispatch('flashMessage', { type: 'danger', msg: json.errorMessage })
        return false
      }

      return true
    },

    async getUserFromResetPasswordToken(resetToken) {
      
      const res = await fetch(`${apiUrl}/auth/forgot-password/user?resetToken=${resetToken}`)

      if (! res.ok) {
        return false
      }

      return await res.json()
    },

    async resetPassword(resetToken, newPassword) {

      const res = await fetch(`${apiUrl}/auth/forgot-password/reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          resetToken,
          newPassword
        })
      })

      if (! res.ok) {
        store.dispatch('flashMessage', { type: 'danger', msg: await res.json() })
        return false
      }

      return true
    },

    async isServerDown() {
      try {
        const res = await fetch(`${apiUrl}`)
        return ! res.ok
      } catch (e) {
        return true
      }
    },

    ////////////////////////
    // ACCOUNT MANAGEMENT //
    ////////////////////////
    
    async getCompanies() {
      const result = await fetchData('accounts/companies');
      return result;
    },

    async createCompany(newCompany) {
      const result = await fetchData(
        'accounts/companies',
        JSON.stringify(newCompany),
        { 'Content-Type': 'application/json' },
        'POST'
      );
      return result !== false
    },

    async getCompany(companyId) {
      const result = await fetchData(`accounts/companies/${companyId}`)
      return result;
    },

    async updateCompany(companyId, updatedCompany) {
      const result = await fetchData(
        `accounts/companies/${companyId}`,
        JSON.stringify(updatedCompany),
        { 'Content-Type': 'application/json' },
        'PATCH'
      )

      return result !== false
    },

    async addExtraToCompany(companyId, thisMonthExtra) {
      const res = await fetch(`${apiUrl}/accounts/companies/${companyId}/extra`, {
        method: 'POST',
        headers: {
          ...getAuthHeader(), 
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ thisMonthExtra })
      })

      if (! res.ok) {
        const json = await res.json()
        store.dispatch('flashMessage', { type: 'danger', msg: json.errorMessage })
        return false 
      }

      return true
    },

    async getUsers() {
      const result = await fetchData('accounts/users')
      return result
    },

    async createUser(newUser) {
      const result = await fetchData(
        'accounts/users',
        JSON.stringify(newUser),
        { 'Content-Type': 'application/json' },
        'POST'
      )

      return result !== false
    },

    async getUser(userId) {
      const result = await fetchData(`accounts/users/${userId}`)
      return result
    },

    async updateUser(userId, updatedUser) {
      const result = await fetchData(
        `accounts/users/${userId}`,
        JSON.stringify(updatedUser),
        { 'Content-Type': 'application/json' },
        'PATCH'
      )
      
      return result !== false
    },

    async deleteUser(userId) {
      const result = await fetchData(
        `accounts/users/${userId}`,
        null,
        {},
        'DELETE'
      )
      
      return result !== false
    },

    //////////////
    // AUDITING //
    //////////////

    async auditFromCsv(csv, batchMeta) {
      const formData = new FormData()
      formData.append('csv', csv)
      Object.keys(batchMeta).forEach(key => {
        if (batchMeta[key] !== null && batchMeta[key] !== undefined) {
          formData.append(key, batchMeta[key])
        }
      })
      const result = await fetchData(
        `audit?engine=luna`,
        formData,
        {},
        'POST'
      )
      return result
    },

    async audit(batchLinks, batchMeta) {
      const result = await fetchData(
        `audit?engine=luna`,
        JSON.stringify({ batchMeta, batchLinks }),
        { 'Content-Type': 'application/json' },
        'POST'
      )
      return result
    },

    async rerunBatch(batchId) {
      const result = await fetchData(
        `audit/${batchId}`,
        null,
        {},
        'PATCH'
      )
      return result !== false
    },

    async rerunAudit(batchId, auditId) {
      const result = await fetchData(
        `audit/${batchId}/${auditId}`,
        null,
        {},
        'PATCH'
      )
      
      return result !== false
    },

    async getAllBatches() {
      const res = await fetch(`${apiUrl}/batches`, {
        headers: { ...getAuthHeader() }
      })
      if (! res.ok) {
        destroyMoonpullSession()
        router.push({ name: 'login' })
        return
      }
      return await res.json()
    },

    async getSingleBatch(batchId) {
      const result = await fetchData(`batches/${batchId}`)
      return result
    },

    async getSingleAudit(batchId, auditId) {
      const result = await fetchData(`batches/${batchId}/audits/${auditId}`)
      return result
    },

    async loadScreenshots(auditId) {
      const result = await fetchData(`screenshots/${auditId}`)
      return result;
    },

    async downloadSingleBatchCSV(batchId) {

      const csv = await fetchData(`batches/${batchId}/csv`)
      if (csv === false) {
        return false
      }

      const a = document.createElement('a')
      document.body.appendChild(a)
      a.setAttribute('style', 'display:none')
      const blob = new Blob([ csv ], { type: 'octet/stream' })
      const url = window.URL.createObjectURL(blob)
      a.href = url
      a.download = `${batchId.substr(-4)}_results.csv`
      a.click()
      window.URL.revokeObjectURL(url)
      return true
    },

    async downloadingTechReportCSV(advertiserName) {
      const csv = await fetchData(`advertisers/${advertiserName}/tech-report`);
      if (csv === false) {
        return false;
      }
    
      const blob = new Blob([csv], { type: 'text/csv' }); // Changed mime type to 'text/csv'
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.setAttribute('hidden', ''); // Changed to 'hidden' to match the second snippet
      a.href = url;
      a.download = `${advertiserName}_tech-report.csv`; // Preserved the dynamic download filename
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a); // Added removal of the anchor tag from the body
      window.URL.revokeObjectURL(url); // Preserved revoking of the URL
    
      return true;
    },
    

    // CMS
    async addClientComment(batchId: string, auditId: string, comment: string) {
      const result = await fetchData(
        `batches/${batchId}/audits/${auditId}/comment`,
        JSON.stringify({ comment }),
        { 'Content-Type': 'application/json' },
        'POST'
      )
      return result !== false
    },

    async addMoonpullComment(batchId: string, auditId: string, comment: string) {
      const result = await fetchData(
        `batches/${batchId}/audits/${auditId}/moonpull-comment`,
        JSON.stringify({ comment }),
        { 'Content-Type': 'application/json' },
        'POST'
      )
      return result !== false
    },

    async getNetworks() {
      const { data: { networkMany } } = await apolloClient.query({
        query: gql`query {
          networkMany(
            sort: {
              name: ASC
            }
          ) {
            id
            moonpullId
            name
            url
            description
            isActive
            successCriteria
            clientComment
            moonpullComment
            createdAt
            updatedAt
            deletedAt
            aliasNetworkId
            ownerId
            owner {
              name
            }
          }
        }`
      })
      return networkMany
    },

    async getNetworksComments() {
      return await fetchData(`cms/networks/comments`)
    },

    async updateNetworkComments(networkComments) {
      return await fetchData(
        `cms/networks/comments`,
        JSON.stringify(networkComments),
        { 'Content-Type': 'application/json' },
        'PATCH'
      )
    },
    
    async getNetwork(networkId) {
      const result = await fetchData(`cms/networks/${networkId}`)
      return result
    },

    async updateNetwork(updatedNetwork) {
      const result = await fetchData(
        `cms/networks/${updatedNetwork.id}`,
        JSON.stringify(updatedNetwork),
        { 'Content-Type': 'application/json' },
        'PATCH'
      )
      return result !== false
    },

    async getNetworkActiveErrors(networkId) {
      return await fetchData(`cms/networks/${networkId}/active-errors`)
    },

    async getNetworkSuccessCriteria() {
      return await fetchData(`cms/networks/success-criteria`)
    },

    async getErrors() {
      const result = await fetchData(`cms/errors`)
      return result
    },

    async getError(errorId) {
      const result = await fetchData(`cms/errors/${errorId}`)
      return result
    },

    async updateError(error) {
      const result = await fetchData(
        `cms/errors/${error.id}`,
        JSON.stringify(error),
        { 'Content-Type': 'application/json' },
        'PATCH'
      )
      return result !== false
    },

    async toggleMoonpullOnlyError(errorId) {
      await fetchData(`cms/errors/${errorId}/toggle-moonpull-only`, {}, {}, 'PATCH')
      return true
    },

    async toggleHideFromDownload(errorId) {
      await fetchData(`cms/errors/${errorId}/toggle-hide-from-download`, {}, {}, 'PATCH')
      return true
    },

    async getNarratives() {
      const result = await fetchData(`cms/narratives`)
      return result
    },

    async getNarrative(narrativeId) {
      const result = await fetchData(`cms/narratives/${narrativeId}`)
      return result
    },

    async getPlatformContent() {
      const { data: { platformContent } } = await apolloClient.query({
        query: gql`
          query PlatformContent {
            platformContent {
              handoverImpactThresholdCritical
              handoverImpactThresholdWarning
              issuesCopyCritical
              issuesCopyWarning
              issuesCopyHealthy
              issuesNoConsent
              issuesNoFpTracking
              issuesOtherTracking
              issuesConsentNotneeded
              issuesChangedStatus
              issuesSameStatus
              issuesUsingMoonpull
              browserCookieCopyChromeFirstPartyPass
              browserCookieCopyChromeThirdPartyPass
              browserCookieCopyChromeFail
              browserCookieCopySafariFirstPartyPass
              browserCookieCopySafariThirdPartyPass
              browserCookieCopySafariFail
              browserCookieCopyFirefoxFirstPartyPass
              browserCookieCopyFirefoxThirdPartyPass
              browserCookieCopyFirefoxFail
              browserCookieCopyEdgeFirstPartyPass
              browserCookieCopyEdgeThirdPartyPass
              browserCookieCopyEdgeFail
              psa
            }
          }
        `,
        fetchPolicy: 'cache-first',
      })
      return platformContent
    },

    async getAdvertisers() {
      const result = await fetchData(`cms/advertisers`)
      return result
    },

    async getAdvertiser(advertiserId) {
      const result = await fetchData(`cms/advertisers/${advertiserId}`)
      return result
    },

    async createAdvertiser(newAdvertiser) {
      const result = await fetchData(
        `cms/advertisers`,
        JSON.stringify(newAdvertiser),
        { 'Content-Type': 'application/json' },
        'POST'
      )
      return result !== false
    },

    async updateAdvertiser(updatedAdvertiser) {
      const result = await fetchData(
        `cms/advertisers/${updatedAdvertiser.id}`,
        JSON.stringify(updatedAdvertiser),
        { 'Content-Type': 'application/json' },
        'PATCH'
      )
      return result !== false
    },

    async deleteAdvertiser(advertiserId) {
      return await fetchData(`cms/advertisers/${advertiserId}`, null, {}, 'DELETE')
    },

    async uploadAdvertisers(networkName, formData) {
      return await fetchData(
        `cms/network/${networkName}/advertisers/csv`,
        formData,
        {},
        'POST'
      )
    },

    auditHasError(audit, error) {
      return audit.moonpullErrors && !! audit.moonpullErrors.find((err: any) => err.moonpullErrorId === error)
    },
    
    downloadCsv(csvData, fileName) {
      const a = document.createElement('a')
      document.body.appendChild(a)
      a.setAttribute('style', 'display:none')
      const blob = new Blob([ csvData ], { type: 'octet/stream' })
      const url = window.URL.createObjectURL(blob)
      a.href = url
      a.download = fileName
      a.click()
      window.URL.revokeObjectURL(url)
    },

    async getLogging() {
      return await fetchData(
        `logging`,
        null,
        {},
        'GET'
      )
    },

    currentUserCompanyIs(companyName){
      return this.currentUserIsSuperAdmin() || this.getCurrentUserSession().company.name === companyName
    },

    async redirectToNewBatchIdIfNecessary(batchId) {
      if (! isUUID(batchId)) {
        const oldBatchLookup = await apolloClient.query({
          query: gql`query OldMongoIDBatchLookup ($oldMongoId: String) {
            batchOne(
              filter: {
                oldMongoId: {
                  equals: $oldMongoId
                }
              }
            ) {
              id
            }
          }`,
          variables: {
            oldMongoId: batchId
          },
          errorPolicy: 'ignore'
        })

        if (oldBatchLookup.data && oldBatchLookup.data.batchOne && oldBatchLookup.data.batchOne.id) {
          router.push({
            name: 'reporting-batch',
            params: {
              batchId: oldBatchLookup.data.batchOne.id
            }
          })
        }
        else {
          router.push({ name: '404' })
        }
      }
    },

    async redirectToNewAuditIdIfNecessary(auditId, name) {
      if (! isUUID(auditId)) {
        const oldAuditLookup = await apolloClient.query({
          query: gql`query OldMongoIDAuditLookup ($oldMongoId: String) {
            auditOne(
              filter: {
                oldMongoId: {
                  equals: $oldMongoId
                }
              }
            ) {
              id
            }
          }`,
          variables: {
            oldMongoId: auditId
          },
          errorPolicy: 'ignore'
        })
        if (oldAuditLookup.data && oldAuditLookup.data.auditOne && oldAuditLookup.data.auditOne.id) {
          router.push({
            name,
            params: {
              auditId: oldAuditLookup.data.auditOne.id
            }
          })
        }
        else {
          router.push({ name: '404' })
        }
      }
    },

    async getAssurance(link) {
      return await fetchData(`client/v1/assurance?link=${encodeURIComponent(link)}`)
    },

    async generateAssurancesFromBatchIds(apiKey, batchIds) {
      return await fetchData(
        `client/v1/assurance`,
        JSON.stringify([
          {
            type: 'BATCH_IDS',
            batchIds
          }
        ]),
        {
          'Content-Type': 'application/json',
          'x-api-key': apiKey
        },
        'POST'
      )
    },

    loadAssuranceEmbedScript(settings) {

      const { VUE_APP_MOONPULL_API_ENDPOINT } = process.env

      const moonpullScriptId = 'moonpull-assurance-script'
      const moonpullScript = document.getElementById(moonpullScriptId)
      if (! moonpullScript) {
        const script = document.createElement('script')
        script.setAttribute('id', moonpullScriptId)
        script.src = `${VUE_APP_MOONPULL_API_ENDPOINT}/client/v1/assurance/embed.js`
        document.body.appendChild(script)
      }

      let success = false
      const moonpullInitInterval = setInterval(() => {
        if (! success) {
          try {
            (window as any).MoonpullAssurance.init({ buttonText: "Assurance Status", ...settings })
            success = true
          } catch (e) {
            console.log(`Error loading link assurance script`, e)
          }
        }
        else {
          clearInterval(moonpullInitInterval)
        }
      }, 500)
    },

    async getOutputReportPDF(auditId) {
      return await fetchData(`audits/${auditId}/pdf`, undefined, undefined, 'GET', true)
    },
    async getValueCalc(mergedata) {
      return await fetchData('value-calc', JSON.stringify(mergedata), { 'Content-Type': 'application/json' }, 'POST', true);
    },
    async getWarningScenarioCalc(mergedata) {
      return await fetchData('value-warning-calc', JSON.stringify(mergedata), { 'Content-Type': 'application/json' }, 'POST', true);
    },
    async getAdvertiserTrackingStatusPDF(auditId) {
      return await fetchData(`audits/${auditId}/advertiser-pdf`, undefined, undefined, 'GET', true)
    },
    async getConsentOnlyPDF(auditId) {
      return await fetchData(`audits/${auditId}/consent-pdf`, undefined, undefined, 'GET', true)
    },
    async getImmediatePDF(auditId) {
      return await fetchData(`audits/${auditId}/immediate-pdf`, undefined, undefined, 'GET', true)
    },
    async getNoCookiePDF(auditId) {
      return await fetchData(`audits/${auditId}/no-cookie-pdf`, undefined, undefined, 'GET', true)
    },
    async getNoAniPDF(auditId) {
      return await fetchData(`audits/${auditId}/no-ani-pdf`, undefined, undefined, 'GET', true)
    },
    async getUserConsentRefreshPDF(auditId) {
      return await fetchData(`audits/${auditId}/user-consent-refresh-pdf`, undefined, undefined, 'GET', true)
    },

    downloadFile(type = 'octet/stream', filename, data) {
      const a = document.createElement('a')
      document.body.appendChild(a)
      a.setAttribute('style', 'display:none')
      const blob = new Blob([ data ], { type })
      const url = window.URL.createObjectURL(blob)
      a.href = url
      a.download = filename
      a.click()
      window.URL.revokeObjectURL(url)
    },

    currentUserHasRole(roleId) {

      const user = this.getCurrentUserSession()
      if (! user) {
        return false
      }

      if (this.currentUserIsSuperAdmin()) {
        return true
      }

      if (user && user.roles && user.roles.some(r => r.id === roleId)) {
        return true
      }
      return false
    },

    currentUserIsNetworkUser() {
      return this.currentUserHasRole('f5c4d40d-c806-4c88-b4b0-799454193a96')
    },

    currentUserIsGlobalNetworkUser() {
      return this.currentUserHasRole('bee9c05b-6e84-4f31-a33c-6e12b7bb58e4')
    },

    currentUserIsMoonpullShareUser() {
      const user = this.getCurrentUserSession()
      if (! user) {
        return false
      }
      return user.company.moonpullShareEnabled && this.currentUserHasRole('4dfdaf67-7726-45a8-a8a2-43ec92c6f863')
    },

    async loadAccessControl() {
      const { data: { platformContentByKey } } = await apolloClient.query({
        query: gql`query AccessControl($key: String!) {
          platformContentByKey(key: $key)
        }`,
        variables: {
          key: 'accessControl',
        }
      })
      accessControl = JSON.parse(platformContentByKey)
    },

    currentUserHasAccessTo(resource) {
      if (this.currentUserIsSuperAdmin()) {
        return true
      }
      if (! accessControl) {
        return false
      }
      const accessControlLower = accessControl[resource] && (accessControl[resource] as string[]).map(item => item.toLowerCase())
      return accessControlLower && (
        accessControlLower.includes(this.getCurrentUserSession().email.toLowerCase()) ||
        accessControlLower.includes(this.getCurrentUserSession().company.name.toLowerCase())
      )
    },

    currentUserHasShareAccess() {
      const currentSession = this.getCurrentUserSession()
      return this.currentUserIsSuperAdmin() || (
        currentSession?.company?.moonpullShareEnabled &&
        currentSession?.roles?.some((r: any) => r.name === 'Moonpull Share User')
      )
    },

    copyToClipboard(content, successMessage) {
      const el = document.createElement('textarea')
      el.value = content
      document.body.appendChild(el)
      el.select()
      document.execCommand('copy')
      document.body.removeChild(el)
      if (successMessage) {
        store.dispatch('flashMessage', { msg: successMessage, type: 'success' })
      }
    },

    nuxtUrl(path) {
      if (window.location.hostname === 'localhost') {
        return `/nuxt-redirect?url=${encodeURIComponent(`http://localhost:3030${path}`)}`
      }
      return `/nuxt-redirect?url=${encodeURIComponent(`https://app2.mnpll.com${path}`)}`
    },

  }
  Vue.prototype.$moonpull = moonpullApi
}
